import { Form, FormikProvider, useFormik } from 'formik'
import React, { FC, useEffect, useRef, useState } from 'react'
import * as Yup from 'yup'
import {
    Alert,
    Box,
    Button,
    Card,
    Dialog,
    MenuItem,
    TextField,
    Typography,
    useTheme,
} from '@mui/material'
import { handlePromise } from '../../../../utils/handlePromisse'
import { getUserProfile, IResponseProfile } from '../../../../services/profileService'
import ModalAddressContent, { ModalAddressContentvalues } from '../ModalAddress'
import { applyMask } from '../../../../utils'
import { useAuth } from '../../../../contexts/auth2'
import LogoDialog from './logo.dialog'
import { deleteLogoUser, uploadLogoMultipart } from '../../../../services/user-logo/user-logo.service'
import { basicMessageDialog, MessageDialog } from '../../../../components/message-dialog'
import { SimpleSnackbar } from '../../../../components/snackbar'

// ── Constants ────────────────────────────────────────────────────────────────

const occupationAreaOptions = [
    'Esteticista',
    'Cosmetologista',
    'Farmaceutica(o)',
    'Biomédica(o)/Bioquímica(o)',
    'Médica(o)',
    'Fisioterapeuta(o)',
    'Outro',
]

// ── Types ────────────────────────────────────────────────────────────────────

export interface IProfileValues {
    fullName: string
    email: string
    phone: string
    occupationArea: string
    cep: string
    city: string
    state: string
    address: string
    district: string
    number: string
}

let initialValues: IProfileValues = {
    fullName: '',
    email: '',
    occupationArea: '',
    phone: '',
    cep: '',
    city: '',
    state: '',
    address: '',
    district: '',
    number: '',
}

export interface ProfileFormProps {
    handleSubmitProp: (idUser: number, entries: Partial<IProfileValues>, setReload: Function) => void
}

// ── Helpers (unchanged) ──────────────────────────────────────────────────────

const updateFormikValues = (formik: any, values: any) => {
    Object.entries(values).map(([key, val]) => (formik.values[key] = val))
}

const updateObjectWithAPIResponse = (state: any, values: any) => {
    Object.entries(values).map(([key, val]) => (state[key] = val))
}

// ── Component ────────────────────────────────────────────────────────────────

const ProfileForm: FC<ProfileFormProps> = ({ handleSubmitProp }) => {
    const theme = useTheme()

    const [state, setState] = React.useState({
        dialog: basicMessageDialog(),
        snackbarMessage: '',
        showSnackbar: false,
    })

    const { user: { idUser } } = useAuth()
    const [showError, setError] = useState(false)
    const [showSuccess, setSuccess] = useState(false)
    const [disableBtnSave, setDisableBtnSave] = useState(true)
    const [openModal, setOpenModal] = useState(false)
    const [openLogoDialog, setOpenLogoDialog] = useState(false)
    const [openLogoPreview, setOpenLogoPreview] = useState(false)
    const [formState, setFormState] = useState<IProfileValues>({ ...initialValues })
    const [reload, setReload] = useState(false)
    const [logoPreview, setLogoPreview] = useState<string | null>(null)
    const [logoFile, setLogoFile] = useState<File | null>(null)
    const [logoOriginal, setLogoOriginal] = useState<string | null>(null)


    // ── Yup schema (unchanged) ───────────────────────────────────────────────

    const ProfileSchema = Yup.object().shape({
        fullName: Yup.string().required('Este campo é obrigatório'),
        email: Yup.string().required('Este campo é obrigatório'),
        occupationArea: Yup.string().required('Este campo é obrigatório'),
        phone: Yup.string()
            .transform((val: string | null) => (!!val ? val.replace(/\D*/gi, '') : ''))
            .required('Este campo é obrigatório')
            .min(10, 'Número de telefone inválido'),
        cep: Yup.string().required('Este campo é obrigatório'),
        city: Yup.string().required('Este campo é obrigatório'),
        state: Yup.string().required('Este campo é obrigatório'),
        address: Yup.string().required('Este campo é obrigatório'),
        district: Yup.string().required('Este campo é obrigatório'),
        number: Yup.string().required('Este campo é obrigatório'),
    })

    const formik = useFormik({
        initialValues: formState,
        validationSchema: ProfileSchema,
        onSubmit: (values, actions) => {},
    })

    const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik

    // ── Load profile (unchanged) ─────────────────────────────────────────────

    useEffect(() => {
        ;(async () => {
            const [result, error] = await handlePromise(getUserProfile(idUser))
            if (!!error) return

            const { data } = result as { data: IResponseProfile }

            const fullName = !!data.name_User ? data.name_User : ''
            const phone = !!data.phone_User ? applyMask('phone').onValue(data.phone_User) : ''
            const occupationArea = !!data.occupation_User ? data.occupation_User : ''
            const email = !!data.email_User ? data.email_User : ''

            const address = !!data.address.street ? data.address.street : ''
            const number = !!data.address.number ? data.address.number : ''
            const city = !!data.address.city ? data.address.city : ''
            const state = !!data.address.state ? data.address.state : ''
            const cep = !!data.address.zipcode ? data.address.zipcode : ''
            const district = !!data.address.neighbourhood ? data.address.neighbourhood : ''
            const logo = data.userLogo ?? null
            setLogoPreview(logo)
            setLogoOriginal(logo)

            updateObjectWithAPIResponse(formState, {
                fullName,
                email,
                phone,
                occupationArea,
                number,
                city,
                district,
                state,
                address,
                cep,
            })
            updateFormikValues(formik, {
                fullName,
                email,
                phone,
                occupationArea,
                number,
                city,
                district,
                state,
                address,
                cep,
            })
            setFormState(formState)
            setDisableBtnSave(!isValidForm(formState))
        })()
    }, [isSubmitting, reload])

    useEffect(() => {
        if (showError) setTimeout(() => setError(false), 2000)
        if (showSuccess) setTimeout(() => setSuccess(false), 2000)
    }, [showError, showSuccess])

    const isLogoChanged = logoPreview !== logoOriginal

    useEffect(() => {
        setDisableBtnSave(
            !isValidForm(formState) || (!logoPreview && !formState.fullName && !formState.email),
        )
    }, [formState, logoPreview, initialValues, formik.values, isSubmitting, reload])

    const isValidForm = (formState: IProfileValues) => {
        return ProfileSchema.isValidSync(formState)
    }

    // ── Handlers (unchanged) ─────────────────────────────────────────────────

    const handleInputChange = (e: any) => {
        if (['phone', 'occupationArea', 'fullName'].includes(e.target.id)) {
            e.target.value = applyMask(e.target.id).onValue(e.target.value)
            const updateObj: any = { ...formState }
            updateObj[e.target.id] = e.target.value
            setFormState(updateObj)
            updateFormikValues(formik, updateObj)
        }
    }

    const handleOccupationAreaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updateObj: any = { ...formState, occupationArea: e.target.value || '' }
        setFormState(updateObj)
        updateFormikValues(formik, updateObj)
    }

    const renderFullAddress = () => {
        const { address, number, district, city, state } = formState
        if (!!address && !!number && !!district && !!city && !!state)
            return `${address}, ${number} - ${district} | ${city} / ${state}`
        return 'ENDEREÇO INCOMPLETO'
    }

    const handleModalClose = (modalState: ModalAddressContentvalues | null, isValidCEP: boolean) => {
        setOpenModal(false)
        if (!!modalState && isValidCEP) {
            const { address, cep, city, district, state, number } = modalState
            updateObjectWithAPIResponse(formState, { address, cep, city, district, state, number })
            setFormState(formState)
            updateFormikValues(formik, formState)
            setDisableBtnSave(!isValidForm(formState))
        }
    }

    const handleSaveProfile = async () => {
        formState.phone = formState.phone.replace(/\D*/gi, '')
        formState.cep = formState.cep.replace(/\D*/gi, '')
        handleSubmitProp(idUser, formState, setReload)
    }

    const handleSaveLogo = async () => {
        if (logoPreview && logoFile) {
            const [_, error] = await handlePromise(uploadLogoMultipart(logoFile))
            if (!!error) {
                const errorMessage =
                    error?.response?.data?.message ||
                    error?.message ||
                    'Ocorreu um erro tente novamente mais tarde.'
                setState(prev => ({ ...prev, showSnackbar: true, snackbarMessage: errorMessage }))
                return
            }
            setState(prev => ({ ...prev, showSnackbar: true, snackbarMessage: 'Logo salvo com sucesso!' }))
            setReload(r => !r)
        }
    }

    const handleDeleteItem = async () => {
        setState(prev => ({ ...prev, dialog: basicMessageDialog() }))

        const [_, error] = await handlePromise(deleteLogoUser())
        if (!!error) {
            if (!!error) {
                setState(prev => ({
                    ...prev,
                    showSnackbar: true,
                    snackbarMessage: error.response.data.message,
                }))
                return
            }
        }

        setLogoPreview(null)
        setLogoOriginal(null)
    }

    const handleDeleteLogo = async () => {
        setState(prev => ({
            ...prev,
            dialog: basicMessageDialog(
                'Tem certeza que deseja remover sua logo?',
                () => handleDeleteItem(),
                true,
            ),
        }))
    }

    // ── Shared input label style ─────────────────────────────────────────────

    const labelSx = {
        '& .MuiInputLabel-root': {
            fontSize: '0.78125rem',
            fontWeight: 600,
            color: 'secondary.main',
        },
    }

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            <MessageDialog {...state.dialog} />
            <SimpleSnackbar
                show={state.showSnackbar}
                message={state.snackbarMessage}
                onDismiss={() => setState(prev => ({ ...prev, showSnackbar: false }))}
            />

            {/* ── Card: Dados pessoais e profissionais ── */}
            <Card
                variant="outlined"
                sx={{
                    borderRadius: '18px',
                    p: { xs: '20px 18px', md: '26px 28px' },
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <Typography
                    sx={{
                        fontFamily: theme.typography.fontFamilyDisplay,
                        fontWeight: 400,
                        fontSize: '1.375rem',
                        color: 'text.primary',
                        mb: 3,
                    }}
                >
                    Dados pessoais e profissionais
                </Typography>

                <FormikProvider value={formik}>
                    <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                gap: 2,
                            }}
                        >
                            {/* Nome */}
                            <TextField
                                id="fullName"
                                fullWidth
                                autoComplete="fullName"
                                type="text"
                                label="NOME"
                                {...getFieldProps('fullName')}
                                onChange={handleInputChange}
                                value={formState.fullName}
                                variant="outlined"
                                color="primary"
                                error={Boolean(touched.fullName && errors.fullName)}
                                helperText={touched.fullName && errors.fullName}
                                sx={labelSx}
                            />

                            {/* Email (read-only) */}
                            <TextField
                                fullWidth
                                autoComplete="email"
                                type="email"
                                label="EMAIL"
                                {...getFieldProps('email')}
                                onChange={() => {}}
                                value={formState.email}
                                variant="outlined"
                                color="primary"
                                disabled
                                error={Boolean(touched.email && errors.email)}
                                helperText={touched.email && errors.email}
                                sx={labelSx}
                            />

                            {/* Telefone */}
                            <TextField
                                fullWidth
                                id="phone"
                                autoComplete="phone"
                                type="text"
                                label="TELEFONE"
                                {...getFieldProps('phone')}
                                onChange={handleInputChange}
                                value={formState.phone}
                                variant="outlined"
                                color="primary"
                                error={Boolean(touched.phone && errors.phone)}
                                helperText={touched.phone && errors.phone}
                                sx={labelSx}
                            />

                            {/* Área de ocupação */}
                            <TextField
                                fullWidth
                                id="occupationArea"
                                autoComplete="occupationArea"
                                type="text"
                                label="ÁREA DE OCUPAÇÃO"
                                {...getFieldProps('occupationArea')}
                                onChange={handleOccupationAreaChange}
                                variant="outlined"
                                color="primary"
                                select
                                inputProps={{ 'data-testid': 'occupationArea', defaultValue: '' }}
                                error={Boolean(touched.occupationArea && errors.occupationArea)}
                                helperText={touched.occupationArea && errors.occupationArea}
                                sx={labelSx}
                            >
                                {occupationAreaOptions.map((e, i) => (
                                    <MenuItem key={i} value={e}>
                                        {e}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {/* Endereço (full row on 2-col grid) */}
                            <TextField
                                fullWidth
                                autoComplete="fullAddress"
                                type="text"
                                label="ENDEREÇO"
                                {...getFieldProps('fullAddress')}
                                onChange={() => {}}
                                value={renderFullAddress()}
                                variant="outlined"
                                color="primary"
                                disabled
                                multiline
                                minRows={2}
                                InputProps={{
                                    endAdornment: (
                                        <span
                                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                            onClick={() => setOpenModal(true)}
                                        >
                                            <Box
                                                component="span"
                                                sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}
                                            >
                                                {/* Home icon inline to avoid extra import */}
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    width={22}
                                                    height={22}
                                                    fill="currentColor"
                                                    aria-hidden
                                                >
                                                    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                                                </svg>
                                            </Box>
                                        </span>
                                    ),
                                }}
                                sx={{
                                    gridColumn: { xs: '1', sm: '1 / -1' },
                                    ...labelSx,
                                    '& input': { cursor: 'pointer' },
                                }}
                            />
                        </Box>
                    </Form>
                </FormikProvider>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        size="large"
                        onClick={handleSaveProfile}
                        disabled={disableBtnSave}
                        variant="contained"
                        sx={{ borderRadius: '8px', textTransform: 'none', minWidth: 180 }}
                    >
                        Salvar alterações
                    </Button>
                </Box>
            </Card>

            {/* ── Card: Logo da receita ── */}
            <Card
                variant="outlined"
                sx={{
                    borderRadius: '18px',
                    p: { xs: '20px 18px', md: '26px 28px' },
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <Typography
                    sx={{
                        fontFamily: theme.typography.fontFamilyDisplay,
                        fontWeight: 400,
                        fontSize: '1.375rem',
                        color: 'text.primary',
                        mb: 2.5,
                    }}
                >
                    Logo da receita
                </Typography>

                <Alert severity="info" sx={{ mb: 2.5 }}>
                    Agora você pode personalizar o seu PDF adicionando sua própria logo! Faça o upload do seu logo em
                    PNG e gere seus novos PDFs personalizados.
                </Alert>

                {/* Preview */}
                {logoPreview && (
                    <Box sx={{ mb: 2.5, textAlign: 'center' }}>
                        <Typography sx={{ fontWeight: 500, mb: 1, fontSize: '0.875rem' }}>
                            Preview da sua logo:
                        </Typography>
                        <Box
                            component="img"
                            src={'data:image/jpeg;base64,' + logoPreview}
                            alt="Logo Preview"
                            onClick={() => setOpenLogoPreview(true)}
                            sx={{
                                maxWidth: 300,
                                maxHeight: 120,
                                borderRadius: '4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                cursor: 'pointer',
                                display: 'block',
                                mx: 'auto',
                            }}
                        />
                    </Box>
                )}

                {/* Action buttons */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', mb: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setOpenLogoDialog(true)}
                        sx={{ borderRadius: '8px', textTransform: 'none' }}
                    >
                        Inserir Logo
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!isLogoChanged}
                        onClick={handleSaveLogo}
                        sx={{ borderRadius: '8px', textTransform: 'none' }}
                    >
                        Salvar Logo
                    </Button>
                    <Button
                        color="error"
                        variant="outlined"
                        disabled={!logoOriginal}
                        onClick={handleDeleteLogo}
                        sx={{ borderRadius: '8px', textTransform: 'none' }}
                    >
                        Deletar Logo
                    </Button>
                </Box>

                {/* Logo preview dialog */}
                <Dialog open={openLogoPreview} onClose={() => setOpenLogoPreview(false)} maxWidth="md">
                    <Box sx={{ p: 2, bgcolor: 'background.paper', textAlign: 'center' }}>
                        {logoPreview && (
                            <Box
                                component="img"
                                src={'data:image/jpeg;base64,' + logoPreview}
                                alt="Logo Expandida"
                                sx={{
                                    maxWidth: 600,
                                    maxHeight: 500,
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 16px rgba(0,0,0,0.12)',
                                }}
                            />
                        )}
                    </Box>
                </Dialog>

                {/* Logo upload dialog */}
                <LogoDialog
                    open={openLogoDialog}
                    onClose={() => setOpenLogoDialog(false)}
                    onLogoChange={base64 => {
                        setLogoPreview(base64.replace(/^data:image\/[a-z]+;base64,/, ''))
                    }}
                    logoPreview={'data:image/jpeg;base64,' + logoPreview}
                    onFileSelect={setLogoFile}
                />
            </Card>

            {/* Address modal (unchanged) */}
            <Dialog open={openModal} onClose={() => {}}>
                <ModalAddressContent
                    address={{
                        address: formState.address,
                        cep: formState.cep,
                        city: formState.city,
                        district: formState.district,
                        number: formState.number,
                        state: formState.state,
                        complement: '',
                    }}
                    onClose={handleModalClose}
                />
            </Dialog>
        </>
    )
}

export default ProfileForm
